/**
 * Integration API Types
 *
 * Shared types for third-party integrations (Jira, Linear, Notion, Asana).
 * Used by both the Chrome extension and dashboard packages.
 */
/**
 * Providers that issues can be exported to. Subset of IntegrationProvider —
 * Slack is a notification target, not an export destination.
 *
 * Declared as a runtime tuple so the type, the label map, and the silent-green
 * invariant test (summarizeExportResults.invariant.test.ts) all read ONE list.
 * Adding a sixth provider here automatically extends the invariant coverage to it.
 */
export const EXPORT_PROVIDERS = ['jira', 'linear', 'notion', 'asana', 'trello', 'clickup', 'github', 'basecamp', 'monday'];
const EXPORT_PROVIDER_LABELS = {
    jira: 'Jira',
    linear: 'Linear',
    notion: 'Notion',
    asana: 'Asana',
    trello: 'Trello',
    clickup: 'ClickUp',
    github: 'GitHub',
    basecamp: 'Basecamp',
    monday: 'monday.com',
};
export function getExportProviderLabel(provider) {
    return EXPORT_PROVIDER_LABELS[provider];
}
/** Prefix marking a provider field that is required upstream but not yet settable via OverlayQA export (surfaced read-only so the user knows to set it in the provider). */
export const UNSUPPORTED_FIELD_NAME_PREFIX = 'Unsupported required field';
export function isUnsupportedField(field) {
    return field.name.startsWith(UNSUPPORTED_FIELD_NAME_PREFIX);
}
/**
 * A created ticket whose screenshot did not attach is "partial". TWO fields
 * carry that signal -- `partial` and `assetsAttached === false` -- and the
 * server sets them together today (_export-core.ts sets partial=true whenever
 * assetsAttached is false). Keying off only one would let a future drift
 * (assetsAttached:false with the partial flag forgotten) render a silent
 * green, the exact OVE-34/OVE-74 failure. Treat either signal as partial so
 * the drift can never resurrect the silent green.
 *
 * THE ONE DEFINITION: summarizeExportResults() below uses this for its count,
 * and it is exported specifically so per-row UI (the extension's
 * ExportModalParts.tsx ResultsList, and useExportExecution.ts's state
 * routing) can call the exact same check instead of re-deriving it from
 * `result.partial` alone -- which is how OVE-84 happened: those two call
 * sites were written before this predicate existed and never picked up the
 * `assetsAttached` OR when it was added. Guarded by
 * summarizeExportResults.invariant.test.ts.
 */
export function isPartialExportResult(result) {
    return result.success && !result.pending && (result.partial === true || result.assetsAttached === false);
}
/**
 * Single source of truth for the export-outcome summary shown to the user.
 *
 * A partial (ticket created, screenshot did not attach) counts as `success`
 * on its row, so a naive "all succeeded" read hides the missing screenshot and
 * shows a green all-clear. That is exactly the silent green OVE-34/OVE-74 exist
 * to kill. Both the dashboard toast (useBulkActions.ts exportSummary) and the
 * extension modal (ExportModal.tsx partial state) build their copy from here so
 * the wording can never diverge between surfaces again.
 */
export function summarizeExportResults(provider, results) {
    const pending = results.filter((r) => r.pending).length;
    const succeeded = results.filter((r) => r.success && !r.pending).length;
    const failed = results.length - succeeded - pending;
    const partial = results.filter(isPartialExportResult).length;
    const label = getExportProviderLabel(provider);
    if (pending > 0) {
        const parts = [`${pending} export${pending === 1 ? '' : 's'} to ${label} in progress`];
        if (succeeded)
            parts.push(`${succeeded} exported${partial ? ` (${partial} without a screenshot)` : ''}`);
        if (failed)
            parts.push(`${failed} failed`);
        return { succeeded, failed, partial, tone: 'warning', message: `${parts.join(', ')}. Try again to check completion.` };
    }
    if (succeeded === 0 && failed === 0) {
        return { succeeded, failed, partial, tone: 'warning', message: 'No issues exported.' };
    }
    // Nothing landed, but rows WERE tried. Counting to zero ("0 exported to Jira,
    // 2 failed.") buries the only thing the user needs, so this names the failure
    // instead. The sentence is the extension's own, ported verbatim rather than
    // invented: it hand-wrote this branch for its whole life while the dashboard,
    // reading the same helper, got the count (docs/toast-trigger-policy.md 5.1).
    if (succeeded === 0) {
        return {
            succeeded,
            failed,
            partial,
            tone: 'warning',
            message: `Export to ${label} failed. No issues were exported.`,
        };
    }
    if (failed > 0) {
        const noShot = partial > 0 ? ` (${partial} without a screenshot)` : '';
        return {
            succeeded,
            failed,
            partial,
            tone: 'warning',
            message: `${succeeded} exported to ${label}, ${failed} failed${noShot}.`,
        };
    }
    if (partial > 0) {
        return {
            succeeded,
            failed,
            partial,
            tone: 'warning',
            message: partial === succeeded && succeeded === 1
                ? `Exported to ${label}, but the screenshot did not attach.`
                : `${succeeded} exported to ${label}, ${partial} without a screenshot.`,
        };
    }
    return {
        succeeded,
        failed,
        partial,
        tone: 'success',
        message: `${succeeded} issue${succeeded === 1 ? '' : 's'} exported to ${label}.`,
    };
}
//# sourceMappingURL=types.js.map